import { useCallback, useMemo } from "react";

import { Close as CloseIcon, OpenInNew } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, Paper, Stack, Typography } from "@mui/material";

import { reportTxt } from "../../../../../../assets/texts/CloudAnalytics";
import { useCustomerId } from "../../../../../../Components/hooks/useCustomerId";
import { type MetricWSnap } from "../../../../../../types";
import { getLetterForIndex } from "../../../../../../utils/string";
import useSegmentTrackEvent from "../../../../../../utils/useSegmentTrackEvent";
import { DRAWER_TYPES } from "../../constants";
import { useDrawerStore } from "../../hooks/useDrawerStore";
import Formula from "./components/Formula";
import MetricVariable from "./components/MetricVariable";
import { getMetricPath, MetricPathType } from "./utils";

const isMetricData = (data: unknown, type: string | null): data is MetricWSnap =>
  type === DRAWER_TYPES.METRIC && data !== null && typeof data === "object" && "data" in data && "ref" in data;

const MetricViewCard = () => {
  const { data, type, closeDrawer } = useDrawerStore();
  const customerId = useCustomerId();
  const { trackEvent } = useSegmentTrackEvent();

  const metricData = isMetricData(data, type) ? data : null;
  const metricVariables = useMemo(() => metricData?.data?.variables || [], [metricData]);
  const variablesArray = useMemo(
    () => metricData?.data?.variables?.map((_, index) => getLetterForIndex(index)) || [],
    [metricData]
  );
  const handleEditMetric = useCallback(() => {
    trackEvent("View Metric", {
      source: "Report Quick View",
      feature: "Cloud Analytics Reports",
    });
    window.open(getMetricPath(customerId, MetricPathType.VIEW, metricData?.ref.id), "_blank");
  }, [customerId, metricData?.ref.id, trackEvent]);

  const handleClose = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {reportTxt.QUICK_VIEW}: {metricData?.data.name}
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Stack sx={{ p: 2 }} gap={3}>
        <Button variant="outlined" startIcon={<OpenInNew />} fullWidth color="primary" onClick={handleEditMetric}>
          {reportTxt.QUICK_VIEW_EDIT_METRIC}
        </Button>

        <Paper
          elevation={0}
          sx={({ palette }) => ({
            backgroundColor: palette.background.default,
            borderRadius: 2,
            border: "1px solid",
            borderColor: palette.divider,
            p: 1.5,
          })}
        >
          <Stack gap={1}>
            <Stack gap={1}>
              {metricVariables.map((metricVar, index) => (
                <MetricVariable key={metricVar.attribution.id} metric={metricVar} letter={variablesArray[index]} />
              ))}
            </Stack>
            <Divider />
            <Formula formula={metricData?.data.formula ?? ""} tokens={variablesArray} />
          </Stack>
        </Paper>
      </Stack>
    </>
  );
};

export default MetricViewCard;
